import React, { useEffect, useMemo, useRef, useState } from "react";

const STORAGE_KEY = "HQ_TICKETS_v1";

export default function ContactHQModal({
  isOpen,
  onClose,
  onSubmit,              // optional callback(payload)
  user = {},            // { name, email } optional
  defaultCategory = "General",
  defaultPriority = "Normal",
}) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [category, setCategory] = useState(defaultCategory);
  const [priority, setPriority] = useState(defaultPriority);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [ccMe, setCcMe] = useState(true);

  const formRef = useRef(null);
  const firstFieldRef = useRef(null);

  // Reset when opened/closed
  useEffect(() => {
    if (isOpen) {
      setName(user.name || "");
      setEmail(user.email || "");
      setCategory(defaultCategory);
      setPriority(defaultPriority);
      setSubject("");
      setMessage("");
      setCcMe(true);
      // focus first field
      setTimeout(() => firstFieldRef.current?.focus(), 0);
    }
  }, [isOpen, user.name, user.email, defaultCategory, defaultPriority]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const templates = useMemo(() => ([
    {
      label: "Billing question",
      subject: "Billing inquiry: invoice/charges",
      body: "Hi HQ, \n\nI have a question about a recent invoice/charge. Could you help clarify the line items and due dates?\n\nThanks!",
    },
    {
      label: "Tech support",
      subject: "Platform issue: cannot access a feature",
      body: "Hi HQ,\n\nI'm having trouble accessing a feature on the platform. Steps to reproduce:\n1) ...\n2) ...\nExpected: ...\nActual: ...\n\nAny help appreciated!",
    },
    {
      label: "Talent ops",
      subject: "Talent change request",
      body: "Hi HQ,\n\nI'd like to request a change regarding a talent assignment. Details:\n- Talent: \n- Project: \n- Requested change: \n\nThanks!",
    },
  ]), []);

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setMessage(t.body);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!subject.trim()) { alert("Please add a subject."); return; }
    if (!message.trim()) { alert("Please write a brief message."); return; }

    const payload = {
      createdAt: new Date().toISOString(),
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      ccMe,
      category,
      priority,
      subject: subject.trim(),
      message: message.trim(),
      status: "NEW",
    };

    // Save to localStorage (simple queue)
    try {
      const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      prev.push(payload);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prev));
    } catch {}

    onSubmit?.(payload);
    alert("Your message has been sent to HQ. We’ll get back to you ASAP.");
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="po-overlay" onClick={onClose} aria-modal="true" role="dialog" aria-labelledby="hq-title">
      <div className="po-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="po-header">
          <h2 id="hq-title" className="po-title">Contact HQ</h2>
          <button className="po-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Body */}
        <form className="po-body" onSubmit={handleSubmit} ref={formRef}>
          {/* Your Info */}
          <section className="po-section">
            <h3 className="po-h3">Your Info</h3>
            <div className="po-grid2">
              <label className="po-field">
                <span className="po-label">Name</span>
                <input
                  ref={firstFieldRef}
                  className="po-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </label>
              <label className="po-field">
                <span className="po-label">Email</span>
                <input
                  className="po-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@company.com"
                />
              </label>
            </div>

            <div className="po-checklist" style={{ marginTop: 8 }}>
              <label className="po-check">
                <input type="checkbox" checked={ccMe} onChange={(e) => setCcMe(e.target.checked)} />
                CC me on this thread
              </label>
            </div>
          </section>

          {/* Request Details */}
          <section className="po-section">
            <h3 className="po-h3">Request Details</h3>
            <div className="po-grid3">
              <label className="po-field">
                <span className="po-label">Category <span className="req-star">*</span></span>
                <select className="po-select" value={category} onChange={(e) => setCategory(e.target.value)} required>
                  <option>General</option>
                  <option>Technical</option>
                  <option>Billing</option>
                  <option>Talent Operations</option>
                  <option>Other</option>
                </select>
              </label>

              <label className="po-field">
                <span className="po-label">Priority</span>
                <select className="po-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
                  <option>Low</option>
                  <option>Normal</option>
                  <option>High</option>
                  <option>Urgent</option>
                </select>
              </label>

              <label className="po-field">
                <span className="po-label">Subject <span className="req-star">*</span></span>
                <input
                  className="po-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Billing inquiry about invoice #1234"
                  required
                />
              </label>
            </div>

            <label className="po-field" style={{ marginTop: 8 }}>
              <span className="po-label">Message <span className="req-star">*</span></span>
              <textarea
                className="po-textarea"
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe the issue or request with relevant details…"
                required
              />
            </label>

            {/* Quick templates reuse .po-chip styling from your CSS */}
            <div className="po-tip">Quick templates:</div>
            <div className="po-chips">
              {templates.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  className="po-chip"
                  onClick={() => applyTemplate(t)}
                  title={`Apply "${t.label}" template`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </section>

          {/* Footer */}
          <div className="po-footer">
            <div className="po-spacer" />
            <button type="button" className="po-btn po-btn-muted" onClick={onClose}>Cancel</button>
            <button type="submit" className="po-btn po-btn-primary">Send to HQ</button>
          </div>
        </form>
      </div>
    </div>
  );
}
